import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'shared/api/client';
import { accessLogKeys } from './keys';

interface AccessLogFilter {
  document?: number[];
  section?: string[];
  user?: number[];
  startDate?: string | null;
  endDate?: string | null;
}

const fetchFilteredAccessLogs = async (filter: AccessLogFilter, documentSuperId: number | string) => {
  const { data } = await api.POST('/access-logs/filter', {
    body: {
      documentSuperId: Number(documentSuperId),
      filter: {
        documentIds: filter.document,
        sectionIds: filter.section,
        userIds: filter.user,
        startDate: filter.startDate,
        endDate: filter.endDate,
      },
    },
  });
  return data;
};

export const useAccessLogs = (filter: AccessLogFilter, documentSuperId: number | string) => {
  return useQuery({
    queryKey: accessLogKeys.list(documentSuperId, filter),
    queryFn: () => fetchFilteredAccessLogs(filter, documentSuperId),
  });
};

export const useAccessLogsFilters = (documentSuperId: number | string) => {
  return useQuery({
    queryKey: accessLogKeys.filters(),

    queryFn: async () => {
      const { data } = await api.GET('/access-logs/filter/{documentSuperId}', {
        params: { path: { documentSuperId: Number(documentSuperId) } },
      });
      return data;
    },

    placeholderData: keepPreviousData,
  });
};

export const useCreateAccessLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: accessLogKeys.create(),

    mutationFn: async ({
      sectionSuperId,
      documentSuperId,
      organizationId,
    }: {
      sectionSuperId: string;
      documentSuperId: number | string;
      organizationId: number;
    }) => {
      const { data } = await api.POST('/access-logs/', {
        body: { sectionSuperId, documentSuperId: Number(documentSuperId), organizationId },
      });
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...accessLogKeys.all],
      });
    },
  });
};
